const MODE_EVIDENCE = "EVIDENCE_QUERY";
const MODE_PATIENT = "PATIENT_CASE";
const ALLOWED_MODES = new Set([MODE_EVIDENCE, MODE_PATIENT]);
const MAX_CITATIONS = 50;
const MAX_POINTS = 20;
const MAX_PROMPTS = 12;

function objectRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function boundedText(value, maxLength) {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text || text.length > maxLength) return null;
  return text;
}

function boundedStrings(value, limit = 20, maxLength = 800) {
  if (!Array.isArray(value)) return [];
  return value
    .slice(0, limit)
    .map((item) => boundedText(item, maxLength))
    .filter(Boolean);
}

export function normalizeRequestDisposition(value) {
  const row = objectRecord(value);
  if (!row) return null;
  const authorityVersion = boundedText(row.authority_version, 100);
  const task = boundedText(row.task, 100);
  if (!authorityVersion || !task || !ALLOWED_MODES.has(row.mode)) return null;
  return {
    authority_version: authorityVersion,
    mode: row.mode,
    task,
    reason_codes: boundedStrings(row.reason_codes, 20, 120),
  };
}

function normalizeCitation(value) {
  const row = objectRecord(value);
  if (!row || !Number.isInteger(row.index) || row.index < 1 || row.index > 10_000) return null;
  const source = boundedText(row.source, 180);
  if (!source) return null;
  return {
    index: row.index,
    source,
    topic: boundedText(row.topic, 160),
    doc_type: boundedText(row.doc_type, 80),
    loc: boundedText(row.loc, 120),
  };
}

function normalizeKeyPoint(value, availableIndices) {
  const row = objectRecord(value);
  if (!row) return null;
  const text = boundedText(row.text, 1200);
  if (!text || !Array.isArray(row.citation_indices) || row.citation_indices.length === 0) return null;
  const indices = [...new Set(row.citation_indices)]
    .filter((item) => Number.isInteger(item) && availableIndices.has(item))
    .slice(0, 10);
  if (indices.length === 0) return null;
  return { text, citation_indices: indices };
}

function normalizePrompt(value) {
  const row = objectRecord(value);
  if (!row) return null;
  const promptId = boundedText(row.prompt_id, 100);
  const title = boundedText(row.title, 160);
  const prompt = boundedText(row.prompt, 1000);
  const task = boundedText(row.task, 100);
  if (!promptId || !title || !prompt || !task) return null;
  return {
    prompt_id: promptId,
    title,
    prompt,
    task,
    tags: boundedStrings(row.tags, 12, 80),
  };
}

export function normalizeEvidenceResponse(payload) {
  const row = objectRecord(payload);
  if (!row) return null;
  const disposition = normalizeRequestDisposition(row.request_disposition);
  if (!disposition || disposition.mode !== MODE_EVIDENCE) return null;
  const contractVersion = boundedText(row.evidence_contract_version, 100);
  const answer = boundedText(row.answer, 5000);
  const answerMarkdown = boundedText(row.answer_markdown, 8000);
  if (!contractVersion || (!answer && !answerMarkdown)) return null;

  const citations = Array.isArray(row.citations)
    ? row.citations.slice(0, MAX_CITATIONS).map(normalizeCitation).filter(Boolean)
    : [];
  const availableIndices = new Set(citations.map((item) => item.index));
  const evidenceKeyPoints = Array.isArray(row.evidence_key_points)
    ? row.evidence_key_points
        .slice(0, MAX_POINTS)
        .map((item) => normalizeKeyPoint(item, availableIndices))
        .filter(Boolean)
    : [];
  if (evidenceKeyPoints.length === 0 || citations.length === 0) return null;

  return {
    response_type: "EVIDENCE",
    evidence_contract_version: contractVersion,
    request_disposition: disposition,
    answer,
    answer_markdown: answerMarkdown,
    evidence_key_points: evidenceKeyPoints,
    limitations: boundedStrings(row.limitations, 10, 800),
    citations,
    related_prompts: Array.isArray(row.related_prompts)
      ? row.related_prompts.slice(0, MAX_PROMPTS).map(normalizePrompt).filter(Boolean)
      : [],
  };
}

export function normalizePromptLibrary(value) {
  const row = objectRecord(value);
  if (!row) return null;
  const version = boundedText(row.version, 100);
  if (!version || !Array.isArray(row.prompts)) return null;
  const prompts = row.prompts.slice(0, 100).map(normalizePrompt).filter(Boolean);
  if (prompts.length === 0) return null;
  return { version, prompts };
}

export const EVIDENCE_MODE = MODE_EVIDENCE;
export const PATIENT_MODE = MODE_PATIENT;
